<template>
  <a-modal
    width="800px"
    :visible="isVisible"
    :footer="null"
    @cancel="onCloseModal"
  >
    <h6>
      {{ $t('store_list_page.mapping_modal.title', { channelName: channel?.sales_channel?.name, storeName: channel?.title }) }}
    </h6>
    <a-spin :spinning="isLoading">
      <div class="table-mapping-container mt-4">
        <table class="table-mapping">
          <thead>
            <tr>
              <th style="width: 34%">
                {{ $t('store_list_page.mapping_modal.table.channel_warehouse') }}
              </th>
              <th style="width: 34%">
                {{ $t('store_list_page.mapping_modal.table.master_warehouse') }}
              </th>
              <th class="text-center" style="width: 12%">
                {{ $t('store_list_page.mapping_modal.table.status') }}
              </th>
              <th class="text-center" style="width: 20%">
                {{ $t('store_list_page.mapping_modal.table.action') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!channelWarehouseList || !channelWarehouseList.length">
              <td colspan="4" class="text-center">
                {{ $t('store_list_page.mapping_modal.table.no_data') }}
              </td>
            </tr>
            <tr v-for="(warehouse, index) in channelWarehouseList" :key="index">
              <td>{{ warehouse.name }}</td>
              <td>
                <template v-if="warehouse.edit">
                  <a-select
                    v-model="warehouse.warehouse_id"
                    class="w-100"
                    show-search
                    :placeholder="$t('store_list_page.mapping_modal.table.select_master_placeholder')"
                    option-filter-prop="children"
                    :options="masterWarehouseList"
                    :filter-option="filterOption"
                    @change="onChangeWarehouseMaster($event, index)"
                  />
                </template>
                <template v-else>
                  <span v-if="warehouse.warehouse_master_name">
                    {{ warehouse.warehouse_master_name }}
                  </span>
                  <span v-else style="font-style: italic; color: #999">
                    {{ $t('store_list_page.mapping_modal.table.not_connected_label') }}
                  </span>
                </template>
              </td>
              <td class="text-center mapping-actions">
                <a-tag :color="warehouse.connected ? 'green' : 'red'" class="w-100">
                  {{ !warehouse.connected ? $t('store_list_page.mapping_modal.table.not_connected_tag') : $t('store_list_page.mapping_modal.table.connected_tag') }}
                </a-tag>
              </td>
              <td class="text-center">
                <template v-if="!warehouse.edit">
                  <a-tooltip v-if="!warehouse.connected" placement="top">
                    <template slot="title">
                      <span>{{ $t('store_list_page.mapping_modal.tooltips.add_sync') }}</span>
                    </template>
                    <a-button :disabled="isLoading" @click="warehouse.edit = true">
                      <a-icon type="plus" />
                    </a-button>
                  </a-tooltip>
                  <a-tooltip v-else placement="top">
                    <template slot="title">
                      <span>{{ $t('store_list_page.mapping_modal.tooltips.disconnect_sync') }}</span>
                    </template>
                    <a-popconfirm
                      placement="bottom"
                      :title="$t('store_list_page.mapping_modal.confirm.disconnect_title')"
                      description=""
                      :ok-text="$t('store_list_page.mapping_modal.confirm.disconnect_ok')"
                      :cancel-text="$t('store_list_page.mapping_modal.confirm.disconnect_cancel')"
                      @confirm="onAction(index, 'delete')"
                    >
                      <a-button :disabled="isLoading">
                        <a-icon type="disconnect" />
                      </a-button>
                    </a-popconfirm>
                  </a-tooltip>
                </template>
                <template v-else>
                  <div class="mapping-action-buttons">
                    <a-tooltip placement="top">
                      <template slot="title">
                        <span>{{ $t('store_list_page.mapping_modal.tooltips.save_sync') }}</span>
                      </template>
                      <a-button :disabled="isLoading" @click="onAction(index, 'save')">
                        <a-icon type="check" />
                      </a-button>
                    </a-tooltip>
                    <a-tooltip placement="top">
                      <template slot="title">
                        <span>{{ $t('store_list_page.mapping_modal.tooltips.cancel') }}</span>
                      </template>
                      <a-button :disabled="isLoading" @click="onCancelEdit(index)">
                        <a-icon type="close" />
                      </a-button>
                    </a-tooltip>
                  </div>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import { getAllWarehouseList } from '@/api/warehouse'
import { getLazadaWarehouseChannelList, adjustLazadaMappingWarehouseChannel } from '@/api/channels/lazada'
import { getTokopediaWarehouseChannelList, adjustTokopediaMappingWarehouseChannel } from '@/api/channels/tokopedia'
import { getShopeeWarehouseChannelList, mappingWarehouseShopeeChannel, unMappingWarehouseShopeeChannel, checkShopeeProductSyncStatus } from '@/api/channels/shopee'
import { getTiktokWarehouseChannelList, adjusTiktoktMappingWarehouseChannel, mappingWarehouseTiktokChannel, unMappingWarehouseTiktokChannel } from '@/api/channels/tiktok'
import { disconnecMappingWarehouseChannel, connectMappingWarehouseChannel, updateMappingWarehouseChannel } from '@/api/channels/index'
import { getBlibliPickupPoint, mappingWarehouseBlibliChannel, unMappingWarehouseBlibliChannel } from '@/api/channels/blibli'
import { getZaloraWarehouseChannelList, mappingWarehouseZaloraChannel, unMappingWarehouseZaloraChannel } from '@/api/channels/zalora'
import { getWarehouseToco, mappingWarehouseTocoChannel, unMappingWarehouseTocoChannel, checkTocoProductSyncStatus } from '@/api/channels/toco'

import { sidebarChannelRefreshMixin } from '@/utils/mixins/sidebarChannelRefreshMixins'

const SHOPEE_WAREHOUSE_RETRY_DELAYS = [500, 1000, 1500, 2000, 2500]
const SHOPEE_WAREHOUSE_EMPTY_SYNC_RETRY_DELAYS = [2000, 4000, 8000]
const TOCO_WAREHOUSE_RETRY_DELAYS = [500, 1000, 1500, 2000, 2500]

export default {
  name: 'ModalUpdateMappingWarehouseV2',
  mixins: [sidebarChannelRefreshMixin],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => {},
    },
    back: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      isVisible: false,
      isLoading: false,
      masterWarehouseList: [],
      channelWarehouseList: [],
    }
  },
  computed: {
    businessId() {
      return this.$store.state.user.restriction_base === 'Warehouse'
        ? Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
        : this.$route.query.business_id
    },
    workspaceId() {
      return this.$route.query.workspace_id
    },
    channel() {
      return this.data
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        this.isVisible = value
        if (value) {
          this.init()
        } else {
          this.masterWarehouseList = []
          this.channelWarehouseList = []
          this.isLoading = false
        }
      },
    },
  },
  methods: {
    async init(showLoading = true, expectedMapping = null) {
      if (showLoading) this.isLoading = true
      await Promise.all([this.fetchWarehouseMaster(), this.fetchWarehouseChannel(expectedMapping)])
        .then(([responseMaster, responseChannel]) => {
          this.masterWarehouseList = responseMaster.map(master => ({
            value: master.id,
            label: master.name,
          }))
          this.channelWarehouseList = responseChannel.map(channel => {
            const warehouseId = channel?.warehouse_id || channel?.warehouse_master_id || null
            const findMasterWarehouse = responseMaster.find(master => master.id === warehouseId)
            return {
              ...channel,
              warehouse_id: warehouseId,
              prev_warehouse_id: warehouseId,
              original_warehouse_id: warehouseId,
              warehouse_master_name: findMasterWarehouse?.name || '',
              connected: !!findMasterWarehouse?.id || !!warehouseId,
              edit: false,
            }
          })
        })
        .catch((err) => {
          if (!showLoading) throw err
          this.masterWarehouseList = []
          this.channelWarehouseList = []
        })
        .finally(() => {
          if (showLoading) this.isLoading = false
        })
    },
    async fetchWarehouseMaster() {
      try {
        const allWarehouses = await getAllWarehouseList({ business_id: this.businessId })

        return allWarehouses
      } catch (err) {
        console.error(err)
        return []
      }
    },
    async fetchWarehouseChannel(expectedMapping = null) {
      let promises = []
      const bodyReq = {
        business_id: this.businessId,
        channel_id: this.channel.id,
        params: {
          check_sync: true,
        },
      }
      if (this.channel.sales_channel.code === 'lazada_id') {
        promises.push(getLazadaWarehouseChannelList(bodyReq))
      } else if (this.channel.sales_channel.code === 'shopee_id') {
        return this.fetchShopeeWarehouseChannel(bodyReq)
      } else if (this.channel.sales_channel.code === 'toco_id') {
        return this.fetchTocoWarehouseChannel(bodyReq, expectedMapping)
      } else if (['tiktok', 'tiktok_id'].includes(this.channel.sales_channel.code)) {
        promises.push(getTiktokWarehouseChannelList(bodyReq))
      } else if (this.channel.sales_channel.code === 'tokopedia_id') {
        promises.push(getTokopediaWarehouseChannelList(bodyReq))
      } else if (this.channel.sales_channel.code === 'blibli_id') {
        promises.push(getBlibliPickupPoint(bodyReq))
      } else if (this.channel.sales_channel.code === 'zalora_id') {
        promises.push(getZaloraWarehouseChannelList(bodyReq))
      }
      return await Promise.all(promises)
        .then(([{ data }]) => {
          if (this.channel.sales_channel.code === 'shopee_id') {
            const shopeeWarehouses = data?.data?.warehouses || data?.response || []
            if (!shopeeWarehouses.length) {
              return [{
                id: null,
                warehouse_channel_id: '',
                warehouse_master_id: null,
                name: 'Default Warehouse',
              }]
            }
            return shopeeWarehouses.map(item => ({
              id: item.id || null,
              warehouse_channel_id: item.id || item.channel_warehouse_id || item.locationId || '',
              warehouse_master_id: item.warehouse_master_id || null,
              warehouse_master_name: item.warehouse_master_name || '',
              name: item.warehouse_name || item.warehouseName || '',
            }))
          } else if (this.channel.sales_channel.code === 'toco_id') {
            const tocoWarehouses = data?.data?.warehouse_list || data?.response || []
            if (!tocoWarehouses.length) {
              return [{
                id: null,
                channel_warehouse_id: '', 
                location_id: '',
                warehouse_master_id: null,
                name: 'Default Warehouse',
              }]
            }
            return tocoWarehouses.map(item => ({
              id: item.warehouse_id || item.id || null,  
              channel_warehouse_id: item.channel_warehouse_id || '', 
              location_id: item.location_id || '',
              warehouse_master_id: item.warehouse_master_id || null,
              warehouse_master_name: item.warehouse_master_name || '',
              name: item.warehouse_name || '',
            }))
          } else if (['tiktok', 'tiktok_id'].includes(this.channel.sales_channel.code)) {
            return data?.data?.warehouse_list || []
          } else if (['zalora_id'].includes(this.channel.sales_channel.code)) {
            if (data?.data?.warehouse_list?.length > 0) {
              const normalizeDataWarehouseListZalora = data?.data?.warehouse_list?.map(item => ({
                ...item,
                name: item.name || 'Default Warehouse',
              }))
              return normalizeDataWarehouseListZalora
            }
            return []
          } else if (this.channel.sales_channel.code === 'blibli_id') {
            const blibliWarehouse = data?.data?.map((item) => {
              return {
                warehouse_id: item.master_warehouse_id,
                channel_warehouse_id: item.id,
                name: item.name,
              }
            })
            return blibliWarehouse
          }
          return data?.data || []
        })
        .catch(() => [])
    },
    async fetchShopeeWarehouseChannel(bodyReq) {
      let shopeeWarehouses = []

      try {
        for (let attempt = 0; this.isVisible; attempt += 1) {
          const { data } = await getShopeeWarehouseChannelList(bodyReq)
          shopeeWarehouses = data?.data?.warehouses || data?.response || []
          const isSyncInProgress = await this.isShopeeWarehouseSyncInProgress(data?.request_id, bodyReq.channel_id)

          if (!isSyncInProgress || !this.isVisible) {
            break
          }

          const retryDelay = SHOPEE_WAREHOUSE_RETRY_DELAYS[Math.min(attempt, SHOPEE_WAREHOUSE_RETRY_DELAYS.length - 1)]
          await new Promise(resolve => setTimeout(resolve, retryDelay))
        }

        if (!shopeeWarehouses.length) {
          return [{
            id: null,
            warehouse_channel_id: '',
            warehouse_master_id: null,
            name: 'Default Warehouse',
          }]
        }

        return shopeeWarehouses.map(item => ({
          id: item.id || null,
          warehouse_channel_id: item.id || item.channel_warehouse_id || item.locationId || '',
          warehouse_master_id: item.warehouse_master_id || null,
          warehouse_master_name: item.warehouse_master_name || '',
          name: item.warehouse_name || item.warehouseName || '',
        }))
      } catch (err) {
        console.error(err)
        return []
      }
    },
    async fetchTocoWarehouseChannel(bodyReq, expectedMapping = null) {
      let tocoWarehouses = []
      let hasExpectedMapping = !expectedMapping

      try {
        for (let attempt = 0; attempt <= TOCO_WAREHOUSE_RETRY_DELAYS.length && this.isVisible; attempt += 1) {
          const { data } = await getWarehouseToco(bodyReq)
          tocoWarehouses = data?.data?.warehouse_list || data?.response || []

          const hasInvalidWarehouseId = tocoWarehouses.some(warehouse => {
            return warehouse.id === null || typeof warehouse.id === 'undefined' || warehouse.id === ''
          })
          hasExpectedMapping = !expectedMapping || tocoWarehouses.some(warehouse => {
            const isSameChannelWarehouse = String(warehouse.channel_warehouse_id) === String(expectedMapping.channelWarehouseId)
            const masterWarehouseId = warehouse.warehouse_master_id || warehouse.warehouse_id || null
            const isExpectedMaster = String(masterWarehouseId || '') === String(expectedMapping.warehouseId || '')
            return isSameChannelWarehouse && isExpectedMaster
          })

          if (((expectedMapping && hasExpectedMapping) || (!expectedMapping && !hasInvalidWarehouseId)) || !this.isVisible) {
            break
          }

          if (attempt < TOCO_WAREHOUSE_RETRY_DELAYS.length) {
            await new Promise(resolve => setTimeout(resolve, TOCO_WAREHOUSE_RETRY_DELAYS[attempt]))
          }
        }

        if (!hasExpectedMapping) {
          throw new Error('Latest Toco warehouse mapping is not available yet')
        }

        if (!tocoWarehouses.length) {
          return [{
            id: null,
            channel_warehouse_id: '',
            location_id: '',
            warehouse_master_id: null,
            name: 'Default Warehouse',
          }]
        }

        return tocoWarehouses.map(item => ({
          id: item.id || null,
          channel_warehouse_id: item.channel_warehouse_id || '',
          location_id: item.location_id || '',
          warehouse_master_id: item.warehouse_master_id || null,
          warehouse_master_name: item.warehouse_master_name || '',
          name: item.warehouse_name || '',
        }))
      } catch (err) {
        console.error(err)
        return []
      }
    },
    async isShopeeWarehouseSyncInProgress(requestId, channelId) {
      if (!requestId) return false

      try {
        const response = await checkShopeeProductSyncStatus({
          business_id: this.businessId,
          workspace_id: this.workspaceId,
          channel_id: channelId,
          request_id: requestId,
        })
        if (response?.data?.status?.toUpperCase() === 'ERROR') return false

        const syncProcesses = response?.data?.data
        if (!Array.isArray(syncProcesses) || !syncProcesses.length) return false

        const syncData = syncProcesses.find(item => item.request_id === requestId)
        return ['RUNNING', 'PENDING'].includes(syncData?.status?.toUpperCase())
      } catch (err) {
        console.error(err)
        return false
      }
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0]?.text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      )
    },
    onChangeWarehouseMaster(value, idx) {
      let duplicateIndex = this.channelWarehouseList.findIndex((channel, index) => channel.warehouse_id === value && index != idx)
      if (duplicateIndex > -1) {
        if (this.channelWarehouseList[duplicateIndex].connected) {
          this.$message.error(this.$t('store_list_page.mapping_modal.messages.duplicate_warehouse'))
          duplicateIndex = idx
        }
        this.channelWarehouseList[duplicateIndex].warehouse_id = this.channelWarehouseList[duplicateIndex].prev_warehouse_id
      } else {
        this.channelWarehouseList[idx].prev_warehouse_id = value
      }
    },
    onAction(index, action) {
      if (!this.channelWarehouseList[index].warehouse_id) {
        return this.$message.error(this.$t('store_list_page.mapping_modal.messages.master_required'))
      }

      if (!this.channel.sales_channel.code) {
        return this.$notification.error({ message: this.$t('store_list_page.mapping_modal.messages.channel_code_missing') })
      }

      if (this.channel.sales_channel.code === 'shopee_id') {
        if (action === 'save') {
          this.onConnectShopee(index)
        } else {
          this.onDisconnectShopee(index)
        }
      } else if (this.channel.sales_channel.code === 'toco_id') {
        if (action === 'save') {
          this.onConnectToco(index)
        } else {
          this.onDisconnectToco(index)
        }
      } else if (['tiktok', 'tiktok_id'].includes(this.channel.sales_channel.code)) {
        this.onActionTiktok(index, action)
      } else if (this.channel.sales_channel.code === 'lazada_id') {
        this.onActionLazada(index, action)
      } else if (this.channel.sales_channel.code === 'tokopedia_id') {
        this.onActionTokopedia(index, action)
      } else if (this.channel.sales_channel.code === 'blibli_id') {
        this.onActionBlibli(index, action)
      } else if (['zalora', 'zalora_id'].includes(this.channel.sales_channel.code)) {
        this.onActionZalora(index, action)
      } else {
        this.$message.info(this.$t('store_list_page.mapping_modal.messages.coming_soon'))
      }
    },
    async onConnectShopee(index) {
      this.isLoading = true
      const { id: channel_id, sales_channel: { code, name } } = this.channel
      const { warehouse_id, warehouse_channel_id, name: warehouse_name } = this.channelWarehouseList[index]
      const bodyReq = {
        business_id: this.businessId,
        data: {
          channel: { id: channel_id, code, name },
          warehouse_id,
          warehouse_channel: {
            id: warehouse_channel_id,
            name: warehouse_name,
          },
        },
      }

      try {
        const response = await mappingWarehouseShopeeChannel(bodyReq)
        const isCompleted = await this.waitForShopeeWarehouseSync(response?.data?.request_id, channel_id, 'MAP_WAREHOUSE')
        if (!isCompleted) {
          this.isLoading = false
          return
        }
        await this.init()
        this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_add') })
        this.$emit('onRefreshList', { channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
        this.notifySidebarChannelRefresh({ channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
      } catch (err) {
        console.error(err)
        this.isLoading = false
      }
    },
    async onDisconnectShopee(index) {
      this.isLoading = true
      const { warehouse_channel_id } = this.channelWarehouseList[index]
      try {
        const response = await unMappingWarehouseShopeeChannel({
          business_id: this.businessId,
          data: {
            channel_id: this.channel.id,
            channel_warehouse_id: warehouse_channel_id,
          },
        })
        const isCompleted = await this.waitForShopeeWarehouseSync(response?.data?.request_id, this.channel.id, 'UNMAP_WAREHOUSE')
        if (!isCompleted) {
          this.isLoading = false
          return
        }
        await this.init()
        this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_disconnect') })
        this.$emit('onRefreshList', { channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
        this.notifySidebarChannelRefresh({ channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
      } catch (err) {
        console.error(err)
        this.isLoading = false
      }
    },
    async waitForShopeeWarehouseSync(requestId, channelId, mode) {
      if (!requestId) {
        throw new Error('Shopee warehouse mapping response has no request_id')
      }

      let emptySyncRetries = 0
      for (let attempt = 0; this.isVisible; attempt += 1) {
        let response
        try {
          response = await checkShopeeProductSyncStatus({
            business_id: this.businessId,
            workspace_id: this.workspaceId,
            channel_id: channelId,
            request_id: requestId,
          })
        } catch (err) {
          console.error(err)
          return true
        }
        if (response?.data?.status?.toUpperCase() === 'ERROR') return true

        const syncProcesses = response?.data?.data
        if (!Array.isArray(syncProcesses) || !syncProcesses.length) {
          if (emptySyncRetries === SHOPEE_WAREHOUSE_EMPTY_SYNC_RETRY_DELAYS.length) {
            return true
          }

          const retryDelay = SHOPEE_WAREHOUSE_EMPTY_SYNC_RETRY_DELAYS[emptySyncRetries]
          emptySyncRetries += 1
          await new Promise(resolve => setTimeout(resolve, retryDelay))
          continue
        }

        const syncData = syncProcesses.find(item => item.request_id === requestId && item.mode === mode)
        const status = syncData?.status?.toUpperCase()

        if (status === 'COMPLETED') return true
        if (status === 'FAILED') {
          throw new Error(syncData?.message || `Shopee warehouse ${mode} failed`)
        }
        if (!['RUNNING', 'PENDING'].includes(status)) return false

        const retryDelay = SHOPEE_WAREHOUSE_RETRY_DELAYS[Math.min(attempt, SHOPEE_WAREHOUSE_RETRY_DELAYS.length - 1)]
        await new Promise(resolve => setTimeout(resolve, retryDelay))
      }
      return false
    },
    async onActionTiktok(idx, action) {
      const selectedWarehouseByIndex = this.channelWarehouseList[idx]
      if (!selectedWarehouseByIndex) {
        return this.$notification.error({
          message: this.$t('store_list_page.mapping_modal.messages.warehouse_not_found'),
        })
      }
      this.isLoading = true
      if (action === 'delete') {
        unMappingWarehouseTiktokChannel({
          business_id: this.businessId,
          data: {
            channel_warehouse_id: selectedWarehouseByIndex.channel_warehouse_id,
            channel_id:  this.channel.id,
          },
        })
        .then(() => {
          this.init()
          this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_disconnect') })
          this.$emit('onRefreshList')
          this.notifySidebarChannelRefresh({ channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
        })
        .catch((err) => {
          console.error(err)
          this.isLoading = false
        })
      } else {
        mappingWarehouseTiktokChannel({
          business_id: this.businessId,
          data: {
            channel_warehouse_id: selectedWarehouseByIndex.channel_warehouse_id,
            warehouse_id: selectedWarehouseByIndex.warehouse_id,
            warehouse_name: selectedWarehouseByIndex.name,
            auto_accept: selectedWarehouseByIndex.auto_accept,
            channel_order_auto_accept_step: selectedWarehouseByIndex.channel_order_auto_accept_step,
            channel_id: this.channel.id,
          },
        })
        .then(() => {
          this.init()
          this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_sync')  })
          this.$emit('onRefreshList')
          this.notifySidebarChannelRefresh({ channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
        })
        .catch((err) => {
          console.error(err)
          this.isLoading = false
        })
      }
    },
    async onActionZalora(idx, action) {
      const selectedWarehouseByIndex = this.channelWarehouseList[idx]
      if (!selectedWarehouseByIndex) {
        return this.$notification.error({
          message: this.$t('store_list_page.mapping_modal.messages.warehouse_not_found'),
        })
      }
      this.isLoading = true
      if (action === 'delete') {
        unMappingWarehouseZaloraChannel({
          business_id: this.businessId,
          data: {
            channel_warehouse_id: selectedWarehouseByIndex.channel_warehouse_id,
            channel_id:  this.channel.id,
          },
        })
        .then(() => {
          this.init()
          this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_disconnect') })
          this.$emit('onRefreshList')
          this.notifySidebarChannelRefresh({ channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
        })
        .catch((err) => {
          console.error(err)
          this.isLoading = false
        })
      } else {
        mappingWarehouseZaloraChannel({
          business_id: this.businessId,
          data: {
            channel_warehouse_id: selectedWarehouseByIndex.channel_warehouse_id,
            warehouse_id: selectedWarehouseByIndex.warehouse_id,
            warehouse_name: selectedWarehouseByIndex.name,
            auto_accept: selectedWarehouseByIndex.auto_accept,
            channel_order_auto_accept_step: selectedWarehouseByIndex.channel_order_auto_accept_step,
            channel_id: this.channel.id,
          },
        })
        .then(() => {
          this.init()
          this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_sync')  })
          this.$emit('onRefreshList')
          this.notifySidebarChannelRefresh({ channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
        })
        .catch((err) => {
          console.error(err)
          this.isLoading = false
        })
      }
    },
    async onActionLazada(idx, action) {
      this.isLoading = true
      await adjustLazadaMappingWarehouseChannel({
        business_id: this.businessId,
        channel_id: this.channel.id,
        data: {
          warehouses: this.channelWarehouseList.map((channel, index) => ({
            warehouse_id: index == idx && action === 'delete'
              ? null
              : channel.warehouse_id,
            channel_warehouse_id: channel.channel_warehouse_id,
            channel_warehouse_name: channel.name,
          })),
        },
      })
      .then(() => {
        this.init()
        this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_sync')  })
        this.$emit('onRefreshList')
        this.notifySidebarChannelRefresh({ channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
      })
      .catch((err) => {
        console.error(err)
        this.isLoading = false
      })
    },
    async onActionBlibli(idx, action) {
      const selectedWarehouseByIndex = this.channelWarehouseList[idx]
      if (!selectedWarehouseByIndex) {
        return this.$notification.error({
          message: this.$t('store_list_page.mapping_modal.messages.warehouse_not_found'),
        })
      }
      this.isLoading = true
      if (action === 'delete') {
        unMappingWarehouseBlibliChannel({
          business_id: this.businessId,
          data: {
            pickup_point_id: selectedWarehouseByIndex.channel_warehouse_id,
            channel_id: this.channel.id,
            store_id: this.channel.detail.store_id,
          },
        })
        .then(() => {
          this.init()
          this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_disconnect') })
          this.$emit('onRefreshList')
          this.notifySidebarChannelRefresh({ channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
        })
        .catch((err) => {
          console.error(err)
          this.isLoading = false
        })
      } else {
        mappingWarehouseBlibliChannel({
          business_id: this.businessId,
          data: {
            pickup_point_id: selectedWarehouseByIndex.channel_warehouse_id,
            warehouse_id: selectedWarehouseByIndex.warehouse_id,
            store_id: this.channel.detail.store_id,
            channel_id: this.channel.id,
          },
        })
        .then(() => {
          this.init()
          this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_sync')  })
          this.$emit('onRefreshList')
          this.notifySidebarChannelRefresh({ channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
        })
        .catch((err) => {
          console.error(err)
          this.isLoading = false
        })
      }
    },
    async onActionTokopedia(idx, action) {
      this.isLoading = true
      await adjustTokopediaMappingWarehouseChannel({
        business_id: this.businessId,
        channel_id: this.channel.id,
        data: {
          warehouses: this.channelWarehouseList.map((channel, index) => ({
            warehouse_id: index == idx && action === 'delete'
              ? null
              : channel.warehouse_id,
            channel_warehouse_id: channel.channel_warehouse_id,
            channel_warehouse_name: channel.name,
          })),
        },
      })
      .then(() => {
        this.init()
        this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_sync')  })
        this.$emit('onRefreshList')
        this.notifySidebarChannelRefresh({ channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
      })
      .catch((err) => {
        console.error(err)
        this.isLoading = false
      })
    },
    async onConnectToco(index) {
      this.isLoading = true
      const { id: channel_id, sales_channel: { code, name } } = this.channel
      const { warehouse_id, channel_warehouse_id, name: warehouse_name } = this.channelWarehouseList[index] 
      const bodyReq = {
        business_id: this.businessId,
        data: {
          channel_id:channel_id,
          warehouse_id: warehouse_id,
          warehouse_channel_id: channel_warehouse_id,
        },
      } 
      try {
        const response = await mappingWarehouseTocoChannel(bodyReq)
        const isCompleted = await this.waitForTocoWarehouseSync(response?.data?.request_id, channel_id, 'MAP_WAREHOUSE')
        if (!isCompleted) {
          this.isLoading = false
          return
        }
        await this.init(false, {
          channelWarehouseId: channel_warehouse_id,
          warehouseId: warehouse_id,
        })
        this.isLoading = false
        this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_add') })
        this.$emit('onRefreshList', {
          channelId: this.channel.id,
          channelCode: this.channel.sales_channel?.code,
          expectedStatus: this.getExpectedChannelStatus(),
        })
        this.notifySidebarChannelRefresh({ channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
      } catch (err) {
        console.error(err)
        this.isLoading = false
      }
    },
    async onDisconnectToco(index) {
      this.isLoading = true
      const { channel_warehouse_id } = this.channelWarehouseList[index]
      try {
        const response = await unMappingWarehouseTocoChannel({
          business_id: this.businessId,
          data: {
            channel_id: this.channel.id,
            warehouse_channel_id: channel_warehouse_id,
          },
        })
        const isCompleted = await this.waitForTocoWarehouseSync(response?.data?.request_id, this.channel.id, 'UNMAP_WAREHOUSE')
        if (!isCompleted) {
          this.isLoading = false
          return
        }
        await this.init(false, {
          channelWarehouseId: channel_warehouse_id,
          warehouseId: null,
        })
        this.isLoading = false
        this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_disconnect') })
        this.$emit('onRefreshList', {
          channelId: this.channel.id,
          channelCode: this.channel.sales_channel?.code,
          expectedStatus: this.getExpectedChannelStatus(),
        })
        this.notifySidebarChannelRefresh({ channelId: this.channel.id, channelCode: this.channel.sales_channel?.code })
      } catch (err) {
        console.error(err)
        this.isLoading = false
      }
    },
    async waitForTocoWarehouseSync(requestId, channelId, mode) {
      if (!requestId) {
        throw new Error('Toco warehouse mapping response has no request_id')
      }

      for (let attempt = 0; attempt <= TOCO_WAREHOUSE_RETRY_DELAYS.length && this.isVisible; attempt += 1) {
        const response = await checkTocoProductSyncStatus({
          business_id: this.businessId,
          workspace_id: this.workspaceId,
          channel_id: channelId,
          request_id: requestId,
        })
        const syncProcesses = response?.data?.data
        const syncData = Array.isArray(syncProcesses)
          ? syncProcesses.find(item => item.requestId === requestId) || syncProcesses[0]
          : syncProcesses
        const status = syncData?.status?.toUpperCase()

        if (status === 'COMPLETED') return true
        if (status === 'FAILED') {
          throw new Error(syncData?.message || `Toco warehouse ${mode} failed`)
        }
        if (attempt === TOCO_WAREHOUSE_RETRY_DELAYS.length) {
          throw new Error(`Toco warehouse ${mode} status is ${status || 'unknown'}`)
        }

        await new Promise(resolve => setTimeout(resolve, TOCO_WAREHOUSE_RETRY_DELAYS[attempt]))
      }
      return false
    },
    getExpectedChannelStatus() {
      return this.channelWarehouseList.length && this.channelWarehouseList.every(warehouse => warehouse.connected)
        ? 'CONNECTED'
        : 'PENDING_WAREHOUSE'
    },
    onCancelEdit(index) {
      this.channelWarehouseList[index].warehouse_id = this.channelWarehouseList[index].original_warehouse_id
      this.channelWarehouseList[index].edit = false
    },
    onCloseModal() {
      this.$emit('onCancel', false)
    },
  },
}
</script>

<style lang="scss">
.table-mapping-container {
  max-height: 500px;
  overflow-y: auto;
}

.table-mapping {
  width: 100%;
  border-collapse: collapse;

  tbody tr:hover td {
    background-color: #e1e5e9;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: #f0f2f4;
    font-weight: 500;
    box-shadow: inset 0 -1px 0 #e4e9f0;
  }

  th, td {
    padding: 11px;
    border-bottom: 1px solid #e4e9f0;
  }

  .mapping-actions {
    white-space: nowrap;
  }

  .mapping-action-buttons {
    display: inline-flex;
    gap: 12px;
    align-items: center;
    justify-content: center;
  }
}
</style>
